import { AncClinicalSummary } from '../components/AncClinicalSummary';
// Encounter detail — Week 3 part 2b (the working surface).
//
// Sections in order:
//   - Header (type / started_at / status pill / Finalize button when open)
//   - Chief complaint + notes (inline edit → PATCH)
//   - Vitals (BP, temp, weight, pulse) — quick-entry row per field
//   - Diagnoses (add row; is_primary toggle; server enforces at-most-one)
//   - Read-only Prescriptions + Service requests sections (Week 4)
//
// Every mutation invalidates ['encounter', id] so the local view stays in
// sync with the server. Finalize also invalidates ['work-list'] so the
// completed row drops off. Consent revocation surfaces as `consent_revoked`
// with a card telling the clinician to re-request access.

import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MedicationRequest, Observation, ServiceRequest } from '@pamosi/shared';
import { ApiError, encountersApi, labsApi, type EncounterDetailRes } from '../lib/api';
import { searchFormulary, type FormularyEntry } from '../lib/formulary';
import { SharedInstructionsPanel } from '../components/SharedInstructionsPanel';
import { TriageCard } from '../components/TriageCard';
import { ProcedureFulfillmentsPanel } from '../components/ProcedureFulfillmentsPanel';
import { AdmitDialog } from '../components/AdmitDialog';
import { EncounterChargesSection } from '../components/EncounterChargesSection';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ENCOUNTER_TYPE_LABELS: Record<string, string> = {
    general: 'General consultation',
    antenatal_visit: 'Antenatal visit',
    postnatal_visit: 'Postnatal visit',
    vaccination: 'Vaccination',
    screening: 'Screening',
    follow_up: 'Follow-up',
    emergency: 'Emergency',
};

export function EncounterDetailScreen() {
    const { id } = useParams<{ id: string }>();

    if (!id || !UUID_RE.test(id)) {
        return <Navigate to="/work-list" replace />;
    }

    const qc = useQueryClient();
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['encounter', id],
        queryFn: () => encountersApi.detail(id),
    });

    if (isLoading) {
        return (
            <div className="pm-card p-8 text-center text-slate-500 text-sm">
                Loading encounter…
            </div>
        );
    }

    if (isError) {
        const code = error instanceof ApiError ? error.code : null;
        const message = code === 'not_found'
            ? 'This encounter no longer exists.'
            : code === 'forbidden_other_clinic'
                ? 'This encounter belongs to another clinic.'
                : 'Could not load this encounter.';
        return (
            <div className="pm-card p-6 max-w-lg">
                <div className="text-slate-800 font-medium">{message}</div>
                <Link
                    to="/work-list"
                    className="text-brand-700 hover:underline text-sm mt-2 inline-block"
                >
                    ← Back to work list
                </Link>
            </div>
        );
    }

    if (!data) return null;
    const { encounter, observations, diagnoses, medication_requests, service_requests, visit_summary } = data;
    const openAdmission = data.open_admission ?? null;
    const typeLabel = ENCOUNTER_TYPE_LABELS[encounter.encounter_type] ?? encounter.encounter_type;
    const writable = encounter.status === 'in_progress' || encounter.status === 'planned';
    // Admit is available after triage/vitals but up through finalization —
    // a doctor may finalize the consult note and then decide to keep the
    // patient under observation. Server also allows planned→admit for pre-
    // booked observation slots.
    const canAdmit = encounter.status === 'in_progress' || encounter.status === 'completed';

    async function invalidate() {
        await qc.invalidateQueries({ queryKey: ['encounter', id] });
    }

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <Link to="/work-list" className="text-brand-700 hover:underline text-sm">
                    ← Work list
                </Link>
                <div className="flex items-start justify-between gap-4 mt-2">
                    <div>
                        <h1 className="text-slate-900 text-2xl font-semibold">{typeLabel}</h1>
                        <div className="text-slate-500 text-sm mt-1 flex items-center gap-2 flex-wrap">
                            <span>Started {new Date(encounter.started_at).toLocaleString()}</span>
                            <span aria-hidden>·</span>
                            <StatusPill status={encounter.status} />
                            {openAdmission && (
                                <>
                                    <span aria-hidden>·</span>
                                    <AdmissionBadge admission={openAdmission} />
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {canAdmit && !openAdmission && (
                            <AdmitTrigger encounterId={id} onAdmitted={invalidate} />
                        )}
                        {writable && <FinalizeButton encounterId={id} onDone={invalidate} />}
                    </div>
                </div>
            </div>

            {visit_summary && (
                <VisitSummaryCard summary={visit_summary} />
            )}

            {/* Phase 2d — nurse triage snapshot sits above the clinical
                sections so the doctor sees acuity + vitals + chief complaint
                before scrolling. Empty state (no triage yet) renders a
                "Start triage" button for nurses only. */}
            <TriageCard encounterId={id} />

            {/* Phase 2b — shared instructions pinned at the top of the
                encounter so clinicians see "latex allergy" / "difficult IV"
                before they start writing. */}
            <AncClinicalSummary patientId={encounter.patient_id} />
            <SharedInstructionsPanel patientId={encounter.patient_id} />

            <HeaderEditor
                encounterId={id}
                chiefComplaint={encounter.chief_complaint}
                notes={encounter.notes}
                writable={writable}
                onSaved={invalidate}
            />

            <VitalsSection
                encounterId={id}
                observations={observations}
                writable={writable}
                onSaved={invalidate}
            />

            <DiagnosesSection
                encounterId={id}
                diagnoses={diagnoses}
                writable={writable}
                onSaved={invalidate}
            />

            <MedicationsSection
                encounterId={id}
                medicationRequests={medication_requests}
                writable={writable}
                onSaved={invalidate}
            />

            <ServiceRequestsSection
                encounterId={id}
                serviceRequests={service_requests}
                writable={writable}
                onSaved={invalidate}
            />

            {/* Original-chart Phase 3.a (2026-10-04) — catalogue-driven lab
                orders. Shows existing lab requests on this encounter and
                lets a doctor order a new one from the clinic catalogue. */}
            <LabOrdersSection
                encounterId={id}
                serviceRequests={service_requests}
                writable={writable}
                onSaved={invalidate}
            />

            {/* Phase 2e — nurse execution surface for ordered procedures.
                Hidden when no procedure requests exist on this encounter. */}
            <ProcedureFulfillmentsPanel
                encounterId={id}
                serviceRequests={service_requests}
            />

            {/* Phase 3.c₁ (2026-10-04) — encounter charges + print receipt.
                Phase 3.c₂ (2026-10-04) — Download PDF / Vault / Email delivery
                + prescription / lab-request PDFs. hasMedications/hasLabs gate
                the two document buttons so they only render when the encounter
                actually has those orders attached. */}
            <EncounterChargesSection
                encounterId={id}
                patientName={null}
                clinicName={null}
                writable={writable}
                hasMedications={medication_requests.length > 0}
                hasLabs={service_requests.some((s) => s.service_type === 'lab')}
            />
        </div>
    );
}

// ─── Visit summary (Week 5) ──────────────────────────────────────────────
// Rendered by the server post-finalize (or on any subsequent read of a
// completed encounter). Shows the clinician the exact text that was
// pushed to the patient's Pamosi Health app.

function VisitSummaryCard({ summary }: { summary: { text: string; oneLine: string } }) {
    const [copied, setCopied] = useState(false);
    async function copy() {
        try {
            await navigator.clipboard.writeText(summary.text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard write can fail on http:// or when the tab is background.
            // No fallback in V0 — clinician can select + copy manually.
        }
    }
    return (
        <div className="pm-card p-5 border-l-4 border-l-emerald-500">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                        Visit summary sent to patient
                    </div>
                    <div className="text-slate-500 text-xs mt-1">
                        Pushed to their Pamosi Health app.
                    </div>
                </div>
                <button
                    type="button"
                    className="pm-btn-outline text-xs"
                    onClick={() => void copy()}
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">
{summary.text}
            </pre>
        </div>
    );
}

// ─── Header editor (chief complaint + notes) ─────────────────────────────

function HeaderEditor({
    encounterId, chiefComplaint, notes, writable, onSaved,
}: {
    encounterId: string;
    chiefComplaint: string | null;
    notes: string | null;
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    const [editing, setEditing] = useState(false);
    const [draftChief, setDraftChief] = useState(chiefComplaint ?? '');
    const [draftNotes, setDraftNotes] = useState(notes ?? '');
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: () => encountersApi.update(encounterId, {
            chiefComplaint: draftChief.trim() ? draftChief.trim() : null,
            notes: draftNotes.trim() ? draftNotes.trim() : null,
        }),
        onSuccess: async () => {
            await onSaved();
            setEditing(false);
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    if (!editing) {
        return (
            <div className="pm-card p-5">
                <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                        Chief complaint & notes
                    </div>
                    {writable && (
                        <button
                            type="button"
                            className="text-brand-700 text-sm hover:underline"
                            onClick={() => {
                                setDraftChief(chiefComplaint ?? '');
                                setDraftNotes(notes ?? '');
                                setError(null);
                                setEditing(true);
                            }}
                        >
                            Edit
                        </button>
                    )}
                </div>
                <div className="mt-3 space-y-3">
                    <ReadOnlyField label="Chief complaint" value={chiefComplaint} />
                    <ReadOnlyField label="Notes" value={notes} />
                </div>
            </div>
        );
    }

    return (
        <div className="pm-card p-5 space-y-3">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Edit chief complaint & notes
            </div>
            <div>
                <label className="pm-label" htmlFor="chief-edit">Chief complaint</label>
                <textarea
                    id="chief-edit"
                    className="pm-input min-h-[64px]"
                    value={draftChief}
                    onChange={(e) => setDraftChief(e.target.value)}
                    disabled={mut.isPending}
                />
            </div>
            <div>
                <label className="pm-label" htmlFor="notes-edit">Notes</label>
                <textarea
                    id="notes-edit"
                    className="pm-input min-h-[120px]"
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    disabled={mut.isPending}
                />
            </div>
            {error && <div className="pm-error">{error}</div>}
            <div className="flex gap-2 justify-end">
                <button
                    type="button"
                    className="pm-btn-outline"
                    onClick={() => { setEditing(false); setError(null); }}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={() => { setError(null); mut.mutate(); }}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
}

function ReadOnlyField({ label, value }: { label: string; value: string | null }) {
    return (
        <div>
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-slate-900 mt-0.5 whitespace-pre-wrap">
                {value?.trim() || <span className="text-slate-400">—</span>}
            </div>
        </div>
    );
}

// ─── Vitals (observations) ───────────────────────────────────────────────

// Codes match the server-side free-form `code` column (see encounters_router
// observation body). BP is split across two rows so the "one code per row"
// server contract holds; we render them side-by-side in the UI.
interface VitalField {
    code: string;
    label: string;
    unit: string;
    placeholder: string;
    min?: number;
    max?: number;
}

const VITAL_FIELDS: VitalField[] = [
    { code: 'systolic_bp',  label: 'Systolic BP',  unit: 'mmHg', placeholder: '120', min: 40,  max: 300 },
    { code: 'diastolic_bp', label: 'Diastolic BP', unit: 'mmHg', placeholder: '80',  min: 20,  max: 200 },
    { code: 'temp_c',       label: 'Temperature',  unit: '°C',   placeholder: '37.0', min: 25, max: 45 },
    { code: 'weight_kg',    label: 'Weight',       unit: 'kg',   placeholder: '70',  min: 0.5, max: 400 },
    { code: 'pulse_bpm',    label: 'Pulse',        unit: 'bpm',  placeholder: '72',  min: 20, max: 300 },
];

function VitalsSection({
    encounterId, observations, writable, onSaved,
}: {
    encounterId: string;
    observations: Observation[];
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    // Show last-value-per-code (observations table is append-only + supersede,
    // and the detail endpoint already filters superseded_by_id is null, so
    // "last row wins" for a given code).
    const latestByCode: Record<string, Observation | undefined> = {};
    for (const o of observations) latestByCode[o.code] = o;

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Vitals & observations
                </div>
                <div className="text-slate-400 text-xs">{observations.length} recorded</div>
            </div>
            {writable ? (
                <div className="grid gap-3 sm:grid-cols-2">
                    {VITAL_FIELDS.map((f) => (
                        <VitalRow
                            key={f.code}
                            field={f}
                            latest={latestByCode[f.code]}
                            encounterId={encounterId}
                            onSaved={onSaved}
                        />
                    ))}
                </div>
            ) : (
                <ObservationList observations={observations} />
            )}
        </div>
    );
}

function VitalRow({
    field, latest, encounterId, onSaved,
}: {
    field: VitalField;
    latest: Observation | undefined;
    encounterId: string;
    onSaved: () => Promise<void>;
}) {
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const mut = useMutation({
        mutationFn: (n: number) => encountersApi.addObservation(encounterId, {
            code: field.code,
            valueNumeric: n,
            unit: field.unit,
        }),
        onSuccess: async () => {
            await onSaved();
            setValue('');
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    function submit() {
        setError(null);
        const n = Number(value);
        if (!Number.isFinite(n)) return setError('Enter a number.');
        if (field.min !== undefined && n < field.min) return setError(`Must be ≥ ${field.min}.`);
        if (field.max !== undefined && n > field.max) return setError(`Must be ≤ ${field.max}.`);
        mut.mutate(n);
    }

    const latestLabel = latest && latest.value_numeric != null
        ? `Last: ${latest.value_numeric}${latest.unit ?? ''}`
        : null;

    return (
        <div className="rounded-lg border border-slate-200 p-3">
            <label className="pm-label" htmlFor={`vital-${field.code}`}>
                {field.label} <span className="text-slate-400 font-normal">({field.unit})</span>
            </label>
            <div className="flex gap-2">
                <input
                    id={`vital-${field.code}`}
                    className="pm-input flex-1"
                    type="number"
                    inputMode="decimal"
                    placeholder={field.placeholder}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={mut.isPending}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); submit(); }
                    }}
                />
                <button
                    type="button"
                    className="pm-btn-outline whitespace-nowrap"
                    onClick={submit}
                    disabled={mut.isPending || !value.trim()}
                >
                    {mut.isPending ? '…' : 'Save'}
                </button>
            </div>
            {latestLabel && !error && (
                <div className="text-slate-500 text-xs mt-1">{latestLabel}</div>
            )}
            {error && <div className="pm-error mt-1">{error}</div>}
        </div>
    );
}

function ObservationList({ observations }: { observations: Observation[] }) {
    if (observations.length === 0) {
        return <div className="text-slate-500 text-sm">No vitals recorded.</div>;
    }
    return (
        <ul className="divide-y divide-slate-100">
            {observations.map((o) => (
                <li key={o.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-800">
                        {VITAL_FIELDS.find((f) => f.code === o.code)?.label ?? o.code}
                    </span>
                    <span className="text-slate-600">
                        {o.value_numeric ?? o.value_text}
                        {o.unit ?? ''}
                    </span>
                </li>
            ))}
        </ul>
    );
}

// ─── Diagnoses ───────────────────────────────────────────────────────────

function DiagnosesSection({
    encounterId, diagnoses, writable, onSaved,
}: {
    encounterId: string;
    diagnoses: import('@pamosi/shared').Diagnosis[];
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    const [description, setDescription] = useState('');
    const [code, setCode] = useState('');
    const [isPrimary, setIsPrimary] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: () => encountersApi.addDiagnosis(encounterId, {
            description: description.trim(),
            code: code.trim() ? code.trim() : null,
            isPrimary,
        }),
        onSuccess: async () => {
            await onSaved();
            setDescription('');
            setCode('');
            setIsPrimary(false);
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    function submit() {
        setError(null);
        if (!description.trim()) return setError('Enter a diagnosis description.');
        mut.mutate();
    }

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Diagnoses
                </div>
                <div className="text-slate-400 text-xs">{diagnoses.length} recorded</div>
            </div>

            {diagnoses.length > 0 && (
                <ul className="divide-y divide-slate-100">
                    {diagnoses.map((d) => (
                        <li key={d.id} className="py-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-slate-900 text-sm">
                                    {d.description}
                                    {d.is_primary && (
                                        <span className="ml-2 text-xs font-medium text-brand-700">Primary</span>
                                    )}
                                </div>
                                {d.code && (
                                    <div className="text-slate-500 text-xs mt-0.5">Code: {d.code}</div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {writable && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                    <div>
                        <label className="pm-label" htmlFor="dx-desc">Add a diagnosis</label>
                        <input
                            id="dx-desc"
                            className="pm-input"
                            type="text"
                            placeholder="e.g. Malaria, uncomplicated"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={mut.isPending}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="pm-label" htmlFor="dx-code">Code (optional)</label>
                            <input
                                id="dx-code"
                                className="pm-input"
                                type="text"
                                placeholder="ICD-10 or local"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                disabled={mut.isPending}
                            />
                        </div>
                        <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={isPrimary}
                                onChange={(e) => setIsPrimary(e.target.checked)}
                                disabled={mut.isPending}
                            />
                            <span>Mark as primary</span>
                        </label>
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="pm-btn-primary"
                            onClick={submit}
                            disabled={mut.isPending}
                        >
                            {mut.isPending ? 'Adding…' : 'Add diagnosis'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Prescriptions ───────────────────────────────────────────────────────

// Prescription writer. Two-step UX:
//   1. Free-text search → formulary picker dropdown (client-side, from
//      /lib/formulary.ts). Click an entry to prefill the whole form.
//   2. Editable form. Clinician can override any field; medicationName is
//      the only required column server-side. Refills defaults to 0.
//
// After save: invalidate ['encounter', id] so the prescriptions list above
// re-renders + form clears. Row goes to status='active' and shows up on
// the pharmacy queue immediately.
function MedicationsSection({
    encounterId, medicationRequests, writable, onSaved,
}: {
    encounterId: string;
    medicationRequests: MedicationRequest[];
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    const [search, setSearch] = useState('');
    const [form, setForm] = useState<Partial<FormularyEntry> & { medicationName?: string; refills?: number }>({});
    const [error, setError] = useState<string | null>(null);

    const suggestions = search.trim() ? searchFormulary(search, 6) : [];

    const mut = useMutation({
        mutationFn: () => encountersApi.addMedication(encounterId, {
            medicationName: (form.medicationName ?? '').trim(),
            dose: form.dose?.trim() || null,
            route: form.route?.trim() || null,
            frequency: form.frequency?.trim() || null,
            duration: form.duration?.trim() || null,
            instructions: form.instructions?.trim() || null,
            dispenseQty: form.dispenseQty?.trim() || null,
            refills: typeof form.refills === 'number' && Number.isInteger(form.refills) ? form.refills : 0,
        }),
        onSuccess: async () => {
            await onSaved();
            setForm({});
            setSearch('');
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    function pickEntry(e: FormularyEntry) {
        setForm({
            medicationName: e.name,
            dose: e.dose,
            route: e.route,
            frequency: e.frequency,
            duration: e.duration,
            instructions: e.instructions ?? '',
            dispenseQty: e.dispenseQty ?? '',
            refills: 0,
        });
        setSearch('');
    }

    function submit() {
        setError(null);
        if (!form.medicationName?.trim()) return setError('Pick a medication or type a custom name.');
        mut.mutate();
    }

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Prescriptions
                </div>
                <div className="text-slate-400 text-xs">{medicationRequests.length} recorded</div>
            </div>

            {medicationRequests.length > 0 && (
                <ul className="divide-y divide-slate-100">
                    {medicationRequests.map((m) => (
                        <li key={m.id} className="py-2 text-sm">
                            <div className="text-slate-900 font-medium">
                                {m.medication_name}
                                <RxStatusPill status={m.status} />
                            </div>
                            <div className="text-slate-500 text-xs mt-0.5">
                                {[m.dose, m.route, m.frequency, m.duration].filter(Boolean).join(' · ') || '—'}
                            </div>
                            {m.instructions && (
                                <div className="text-slate-500 text-xs mt-0.5 whitespace-pre-wrap">
                                    {m.instructions}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {writable && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                    <div>
                        <label className="pm-label" htmlFor="rx-search">Search formulary</label>
                        <input
                            id="rx-search"
                            className="pm-input"
                            type="text"
                            placeholder="e.g. amoxicillin, paracetamol, coartem"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={mut.isPending}
                        />
                        {suggestions.length > 0 && (
                            <ul className="mt-1 max-h-52 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
                                {suggestions.map((s) => (
                                    <li key={s.name}>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                                            onClick={() => pickEntry(s)}
                                        >
                                            <div className="text-slate-900 font-medium">{s.name}</div>
                                            <div className="text-slate-500 text-xs">
                                                {s.category} · {s.dose} {s.route} · {s.frequency} · {s.duration}
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="pm-label" htmlFor="rx-name">Medication</label>
                            <input
                                id="rx-name"
                                className="pm-input"
                                type="text"
                                placeholder="e.g. Amoxicillin"
                                value={form.medicationName ?? ''}
                                onChange={(e) => setForm({ ...form, medicationName: e.target.value })}
                                disabled={mut.isPending}
                            />
                        </div>
                        <RxField label="Dose" value={form.dose} onChange={(v) => setForm({ ...form, dose: v })} disabled={mut.isPending} placeholder="500 mg" />
                        <RxField label="Route" value={form.route} onChange={(v) => setForm({ ...form, route: v })} disabled={mut.isPending} placeholder="oral" />
                        <RxField label="Frequency" value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })} disabled={mut.isPending} placeholder="twice daily" />
                        <RxField label="Duration" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} disabled={mut.isPending} placeholder="5 days" />
                        <RxField label="Dispense qty" value={form.dispenseQty} onChange={(v) => setForm({ ...form, dispenseQty: v })} disabled={mut.isPending} placeholder="10 tablets" />
                        <div>
                            <label className="pm-label" htmlFor="rx-refills">Refills</label>
                            <input
                                id="rx-refills"
                                className="pm-input"
                                type="number"
                                min={0}
                                max={12}
                                step={1}
                                value={form.refills ?? 0}
                                onChange={(e) => setForm({ ...form, refills: Math.max(0, Number.parseInt(e.target.value || '0', 10)) })}
                                disabled={mut.isPending}
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="pm-label" htmlFor="rx-instr">Instructions</label>
                            <textarea
                                id="rx-instr"
                                className="pm-input min-h-[60px]"
                                placeholder="Take with food."
                                value={form.instructions ?? ''}
                                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                                disabled={mut.isPending}
                            />
                        </div>
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <div className="flex justify-end gap-2">
                        {form.medicationName && (
                            <button
                                type="button"
                                className="pm-btn-outline"
                                onClick={() => { setForm({}); setError(null); }}
                                disabled={mut.isPending}
                            >
                                Clear
                            </button>
                        )}
                        <button
                            type="button"
                            className="pm-btn-primary"
                            onClick={submit}
                            disabled={mut.isPending}
                        >
                            {mut.isPending ? 'Adding…' : 'Add prescription'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function RxField({
    label, value, onChange, disabled, placeholder,
}: {
    label: string;
    value: string | undefined;
    onChange: (v: string) => void;
    disabled: boolean;
    placeholder: string;
}) {
    return (
        <div>
            <label className="pm-label">{label}</label>
            <input
                className="pm-input"
                type="text"
                placeholder={placeholder}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
            />
        </div>
    );
}

function RxStatusPill({ status }: { status: string }) {
    const tone = status === 'active'
        ? 'bg-brand-50 text-brand-700'
        : status === 'completed'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-slate-100 text-slate-600';
    const label = status === 'active' ? 'Pending'
        : status === 'completed' ? 'Dispensed'
        : status;
    return (
        <span className={'ml-2 text-xs font-medium px-2 py-0.5 rounded-full ' + tone}>
            {label}
        </span>
    );
}

// ─── Service requests (labs / imaging / procedures / referrals) ─────────

// Server allowlist for `service_type`. Kept in sync with SERVICE_TYPES in
// apps/api/src/modules/clinician/encounters_router.ts. Order chosen so the
// most-common request in a Nigerian primary-care visit (lab) is first.
// Original-chart Phase 3.a (2026-10-04) — 'lab' moved to its own catalogue-
// driven form (LabOrderForm below) so lab orders flow through the lab
// state machine. Procedures / imaging / referrals keep the free-text path.
const SERVICE_TYPE_OPTIONS: Array<{
    value: 'imaging' | 'procedure' | 'referral';
    label: string;
    placeholder: string;
    targetHint: string;
}> = [
    { value: 'imaging',  label: 'Imaging',  placeholder: 'e.g. Chest X-ray, abdominal ultrasound',   targetHint: 'Facility / department (optional)' },
    { value: 'procedure',label: 'Procedure',placeholder: 'e.g. Wound dressing, IV cannulation',      targetHint: 'Location (optional)' },
    { value: 'referral', label: 'Referral', placeholder: 'e.g. Refer to obstetrician for HTN review', targetHint: 'Referred clinic / specialist' },
];

function ServiceRequestsSection({
    encounterId, serviceRequests, writable, onSaved,
}: {
    encounterId: string;
    serviceRequests: ServiceRequest[];
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    const [serviceType, setServiceType] = useState<'imaging' | 'procedure' | 'referral'>('procedure');
    const [description, setDescription] = useState('');
    const [code, setCode] = useState('');
    const [target, setTarget] = useState('');
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: () => encountersApi.addServiceRequest(encounterId, {
            serviceType,
            description: description.trim(),
            code: code.trim() ? code.trim() : null,
            target: target.trim() ? target.trim() : null,
        }),
        onSuccess: async () => {
            await onSaved();
            setDescription('');
            setCode('');
            setTarget('');
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    function submit() {
        setError(null);
        if (!description.trim()) return setError('Enter a description.');
        mut.mutate();
    }

    const activeOption = SERVICE_TYPE_OPTIONS.find((o) => o.value === serviceType)!;

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Labs, imaging & referrals
                </div>
                <div className="text-slate-400 text-xs">{serviceRequests.length} requested</div>
            </div>

            {serviceRequests.length > 0 && (
                <ul className="divide-y divide-slate-100">
                    {serviceRequests.map((s) => (
                        <li key={s.id} className="py-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-slate-900 text-sm">
                                    <span className="uppercase text-xs font-semibold text-slate-500 mr-2">
                                        {s.service_type}
                                    </span>
                                    {s.description}
                                </div>
                                <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                                    {s.code && <span>Code: {s.code}</span>}
                                    {s.code && s.target && <span aria-hidden>·</span>}
                                    {s.target && <span>→ {s.target}</span>}
                                    {(s.code || s.target) && <span aria-hidden>·</span>}
                                    <span className="capitalize">{s.status}</span>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {writable && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                    <div>
                        <label className="pm-label" htmlFor="sr-type">Add a request</label>
                        <div id="sr-type" className="flex flex-wrap gap-2">
                            {SERVICE_TYPE_OPTIONS.map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => setServiceType(o.value)}
                                    className={
                                        'px-3 py-1.5 rounded-md text-sm font-medium transition border ' +
                                        (serviceType === o.value
                                            ? 'bg-brand-50 border-brand-300 text-brand-800'
                                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')
                                    }
                                    disabled={mut.isPending}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="sr-desc">Description</label>
                        <input
                            id="sr-desc"
                            className="pm-input"
                            type="text"
                            placeholder={activeOption.placeholder}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={mut.isPending}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="pm-label" htmlFor="sr-code">Code (optional)</label>
                            <input
                                id="sr-code"
                                className="pm-input"
                                type="text"
                                placeholder="LOINC / CPT / local"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                disabled={mut.isPending}
                            />
                        </div>
                        <div>
                            <label className="pm-label" htmlFor="sr-target">{activeOption.targetHint}</label>
                            <input
                                id="sr-target"
                                className="pm-input"
                                type="text"
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                                disabled={mut.isPending}
                            />
                        </div>
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="pm-btn-primary"
                            onClick={submit}
                            disabled={mut.isPending}
                        >
                            {mut.isPending ? 'Adding…' : `Add ${activeOption.label.toLowerCase()} request`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Original-chart Phase 3.a — Lab orders ───────────────────────────────
// Catalogue-driven lab order form + list of lab requests on this encounter.
// Separate from ServiceRequestsSection because lab orders ride on their
// own state machine (service_requests.lab_status) and go to the Labs tab
// for the nurse to work through collect/process/result/release.

function LabOrdersSection({
    encounterId, serviceRequests, writable, onSaved,
}: {
    encounterId: string;
    serviceRequests: ServiceRequest[];
    writable: boolean;
    onSaved: () => Promise<void>;
}) {
    const [labTestId, setLabTestId] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const { data: catalogue } = useQuery({
        queryKey: ['lab-catalogue-active'],
        queryFn: () => labsApi.listCatalogue(),
    });

    const existing = serviceRequests.filter((s) => s.service_type === 'lab');

    const order = useMutation({
        mutationFn: () => labsApi.order({
            encounter_id: encounterId,
            lab_test_id: labTestId,
            note: note.trim() || null,
        }),
        onSuccess: async () => {
            await onSaved();
            setLabTestId('');
            setNote('');
        },
        onError: (err) => setError(prettyWriteError(err)),
    });

    function submit() {
        setError(null);
        if (!labTestId) return setError('Select a test from the catalogue.');
        order.mutate();
    }

    return (
        <div className="pm-card p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                    Lab orders
                </div>
                <div className="text-slate-400 text-xs">{existing.length} on this encounter</div>
            </div>

            {existing.length > 0 && (
                <ul className="divide-y divide-slate-100">
                    {existing.map((s) => (
                        <li key={s.id} className="py-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-slate-900 text-sm">
                                    <span className="uppercase text-xs font-semibold text-slate-500 mr-2">LAB</span>
                                    {s.description}
                                </div>
                                <div className="text-slate-500 text-xs mt-0.5 capitalize">
                                    {s.code && <>Code: {s.code} · </>}{s.status}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {writable && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                    <div>
                        <label className="pm-label" htmlFor="lab-order-test">Test</label>
                        <select
                            id="lab-order-test"
                            className="pm-input"
                            value={labTestId}
                            onChange={(e) => setLabTestId(e.target.value)}
                            disabled={order.isPending || !catalogue}
                        >
                            <option value="">
                                {catalogue && catalogue.tests.length === 0
                                    ? 'No tests in catalogue — add one on the Labs tab'
                                    : 'Select a test…'}
                            </option>
                            {catalogue?.tests.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name} ({t.code})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="lab-order-note">Clinical note (optional)</label>
                        <input
                            id="lab-order-note"
                            className="pm-input"
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. fasting, suspect anaemia"
                            disabled={order.isPending}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="pm-btn-primary"
                            onClick={submit}
                            disabled={order.isPending || !labTestId}
                        >
                            {order.isPending ? 'Ordering…' : 'Order lab'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Phase 3: Admit ──────────────────────────────────────────────────────

function AdmitTrigger({ encounterId, onAdmitted }: { encounterId: string; onAdmitted: () => Promise<void> }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                className="pm-btn-outline"
                onClick={() => setOpen(true)}
            >
                Admit to observation
            </button>
            {open && (
                <AdmitDialog
                    encounterId={encounterId}
                    onClose={() => setOpen(false)}
                    onAdmitted={() => {
                        setOpen(false);
                        void onAdmitted();
                    }}
                />
            )}
        </>
    );
}

function AdmissionBadge({ admission }: { admission: NonNullable<EncounterDetailRes['open_admission']> }) {
    const minsAgo = Math.max(
        0,
        Math.floor((Date.now() - new Date(admission.admitted_at).getTime()) / 60_000),
    );
    const locationBit = admission.location_label ? ` in ${admission.location_label}` : '';
    return (
        <Link
            to={`/queues`}
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200"
            title="Open the admissions board"
        >
            Observing{locationBit} · {minsAgo} min
        </Link>
    );
}

// ─── Finalize ────────────────────────────────────────────────────────────

function FinalizeButton({ encounterId, onDone }: { encounterId: string; onDone: () => Promise<void> }) {
    const qc = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const mut = useMutation({
        mutationFn: () => encountersApi.finalize(encounterId),
        onSuccess: async () => {
            await Promise.all([onDone(), qc.invalidateQueries({ queryKey: ['work-list'] })]);
        },
        onError: (err) => setError(prettyWriteError(err)),
    });
    // Simple confirm — a modal would be overkill for V0; nurses can undo by
    // re-opening the encounter row (Week 6 will add an explicit reopen action).
    function submit() {
        if (!window.confirm('Finalize this encounter? Vitals, diagnoses, and notes will be locked.')) return;
        setError(null);
        mut.mutate();
    }
    return (
        <div className="text-right">
            <button
                type="button"
                className="pm-btn-primary"
                onClick={submit}
                disabled={mut.isPending}
            >
                {mut.isPending ? 'Finalizing…' : 'Finalize'}
            </button>
            {error && <div className="pm-error mt-2">{error}</div>}
        </div>
    );
}

// ─── Shared bits ─────────────────────────────────────────────────────────

function StatusPill({ status }: { status: string }) {
    const tone = status === 'in_progress'
        ? 'bg-brand-50 text-brand-700'
        : status === 'completed'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-slate-100 text-slate-600';
    const label = status === 'in_progress' ? 'In progress'
        : status === 'completed' ? 'Completed'
        : status;
    return (
        <span className={'text-xs font-medium px-2 py-0.5 rounded-full ' + tone}>
            {label}
        </span>
    );
}

function SectionCard({ title, count, empty }: { title: string; count: number; empty: string }) {
    return (
        <div className="pm-card p-4">
            <div className="flex items-center justify-between">
                <div className="text-slate-900 font-medium">{title}</div>
                <div className="text-slate-400 text-xs">{count}</div>
            </div>
            <div className="text-slate-500 text-sm mt-1">
                {count === 0 ? empty : `${count} recorded`}
            </div>
        </div>
    );
}

function prettyWriteError(err: unknown): string {
    if (err instanceof ApiError) {
        switch (err.code) {
            case 'consent_revoked': return 'The patient revoked consent. Ask them to re-approve on WhatsApp before continuing.';
            case 'encounter_not_writable':
            case 'encounter_completed': return 'This encounter is finalized. Reopen it from the work list to keep editing.';
            case 'encounter_cancelled': return 'This encounter was cancelled.';
            case 'invalid_description': return 'Enter a diagnosis description.';
            case 'value_required': return 'Enter a value.';
        }
    }
    return 'Could not save. Try again.';
}
